import React, { useContext, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { ShopContext } from '../context/ShopContext';
import { CloseIcon } from './Icons';
import {
  checkUserExistsApi,
  sendOtpApi,
  verifyOtpApi,
  sendMobileOtpApi,
  verifyMobileOtpApi
} from '../services/api';
import './AuthModal.css';

const AuthModal = () => {
  const { authModalOpen, setAuthModalOpen, loginUser, registerUser, showToast } = useContext(ShopContext);

  const [activeTab, setActiveTab] = useState('login'); // login or register
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [emailCode, setEmailCode] = useState('');
  const [mobileCode, setMobileCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [mobileSent, setMobileSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [otpBusy, setOtpBusy] = useState('');

  if (!authModalOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email') {
      setEmailCode('');
      setEmailSent(false);
      setEmailVerified(false);
    }
  };

  const handlePhoneChange = (phone = '') => {
    setFormData(prev => ({ ...prev, phone }));
    setMobileCode('');
    setMobileSent(false);
    setMobileVerified(false);
  };

  const handleClose = () => {
    setAuthModalOpen(false);
    setActiveTab('login');
    setEmailCode('');
    setMobileCode('');
    setEmailSent(false);
    setMobileSent(false);
    setEmailVerified(false);
    setMobileVerified(false);
  };

  const sendEmailCode = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) return showToast('Enter your email address first.', 'error');
    setOtpBusy('email-send');
    try {
      await sendOtpApi({ email });
      setEmailSent(true);
      showToast('Verification code sent to your email.', 'success');
    } catch (err) {
      showToast(err.message || 'Unable to send email code.', 'error');
    } finally {
      setOtpBusy('');
    }
  };

  const verifyEmailCode = async () => {
    setOtpBusy('email-verify');
    try {
      await verifyOtpApi({ email: formData.email, otp: emailCode });
      setEmailVerified(true);
      showToast('Email verified.', 'success');
    } catch (err) {
      showToast(err.message || 'Email verification failed.', 'error');
    } finally {
      setOtpBusy('');
    }
  };

  const sendMobileCode = async () => {
    const phone = formData.phone.trim();
    if (!phone) return showToast('Enter your mobile number first.', 'error');
    setOtpBusy('mobile-send');
    try {
      const res = await sendMobileOtpApi({ phone });
      setMobileSent(true);
      if (res && res.otp) {
        setMobileCode(res.otp);
        showToast(`Verification code: ${res.otp}`, 'success');
      } else {
        showToast('Verification code sent by SMS.', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Unable to send mobile code.', 'error');
    } finally {
      setOtpBusy('');
    }
  };

  const verifyMobileCode = async () => {
    setOtpBusy('mobile-verify');
    try {
      await verifyMobileOtpApi({ phone: formData.phone, otp: mobileCode });
      setMobileVerified(true);
      showToast('Mobile number verified.', 'success');
    } catch (err) {
      showToast(err.message || 'Mobile verification failed.', 'error');
    } finally {
      setOtpBusy('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, fullName, phone, password } = formData;

    if (activeTab === 'login') {
      if (!email || !password) {
        showToast('Please enter your email and password.', 'error');
        return;
      }
      loginUser(email, password);
      return;
    }

    if (!fullName || !email || !password) {
      showToast('Please fill out your name, email, and password.', 'error');
      return;
    }
    if (!emailVerified && !mobileVerified) {
      showToast('Please verify either your email address or mobile number to continue.', 'error');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = phone.trim();

    try {
      const checkRes = await checkUserExistsApi({ email: cleanEmail, phone: cleanPhone });
      if (checkRes && checkRes.exists) {
        showToast('Email or phone already registered. Please sign in.', 'error');
        return;
      }
    } catch (err) {
      console.warn('Pre-check notice:', err.message);
    }

    try {
      showToast('Registering account...', 'info');
      const success = await registerUser(cleanEmail, fullName, cleanPhone, password);
      if (success) {
        setFormData({ fullName: '', email: '', phone: '', password: '' });
      }
    } catch (err) {
      showToast(err.message || 'Registration failed. Please try again.', 'error');
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal-card glass animate-fade-in" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button onClick={handleClose} className="btn-icon auth-modal-close" aria-label="Close modal">
          <CloseIcon size={18} />
        </button>

        {/* Tabs Selector */}
        <div className="auth-modal-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('login')}
            className={`auth-tab-link ${activeTab === 'login' ? 'active' : ''}`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('register')}
            className={`auth-tab-link ${activeTab === 'register' ? 'active' : ''}`}
          >
            Create Account
          </button>
        </div>

        {/* Auth Forms */}
        <form onSubmit={handleSubmit} className="auth-modal-form">
          {activeTab === 'register' && (
            <div className="form-group">
              <label htmlFor="auth-fullname">Name</label>
              <input
                type="text"
                id="auth-fullname"
                name="fullName"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleInputChange}
                className="input-field"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="auth-email">Email Address</label>
            <input
              type="email"
              id="auth-email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleInputChange}
              className="input-field"
              required
            />
          </div>

          {activeTab === 'register' && (
            <div className="auth-otp-controls">
              {emailVerified ? (
                <span className="auth-otp-verified">Email verified ✓</span>
              ) : mobileVerified ? (
                <span className="auth-otp-hint" style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 500 }}>
                  ✓ Mobile already verified — Email code not required
                </span>
              ) : (
                <>
                  <button type="button" className="auth-otp-action" onClick={sendEmailCode} disabled={Boolean(otpBusy)}>
                    {otpBusy === 'email-send' ? 'Sending...' : emailSent ? 'Resend email code' : 'Send email code'}
                  </button>
                  {emailSent && (
                    <div className="auth-otp-entry">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength="6"
                        aria-label="Email verification code"
                        placeholder="6-digit code"
                        value={emailCode}
                        onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="input-field"
                      />
                      <button type="button" className="auth-otp-action" onClick={verifyEmailCode} disabled={Boolean(otpBusy) || emailCode.length !== 6}>
                        {otpBusy === 'email-verify' ? 'Checking...' : 'Verify email'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'register' && (
            <div className="form-group">
              <label htmlFor="auth-phone">Phone Number {emailVerified ? '(Optional)' : ''}</label>
              <PhoneInput
                id="auth-phone"
                defaultCountry="IN"
                value={formData.phone || undefined}
                onChange={handlePhoneChange}
                className="auth-phone-input"
                countrySelectProps={{ 'aria-label': 'Select country' }}
                numberInputProps={{
                  required: !emailVerified,
                  autoComplete: 'tel-national',
                  'aria-label': 'Phone number'
                }}
              />
            </div>
          )}

          {activeTab === 'register' && (
            <div className="auth-otp-controls">
              {mobileVerified ? (
                <span className="auth-otp-verified">Mobile number verified ✓</span>
              ) : emailVerified ? (
                <span className="auth-otp-hint" style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 500 }}>
                  ✓ Email already verified — SMS code not required
                </span>
              ) : (
                <>
                  <button type="button" className="auth-otp-action" onClick={sendMobileCode} disabled={Boolean(otpBusy)}>
                    {otpBusy === 'mobile-send' ? 'Sending...' : mobileSent ? 'Resend SMS code' : 'Send SMS code'}
                  </button>
                  {mobileSent && (
                    <div className="auth-otp-entry">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength="10"
                        aria-label="Mobile verification code"
                        placeholder="Verification code"
                        value={mobileCode}
                        onChange={(event) => setMobileCode(event.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="input-field"
                      />
                      <button type="button" className="auth-otp-action" onClick={verifyMobileCode} disabled={Boolean(otpBusy) || mobileCode.length < 4}>
                        {otpBusy === 'mobile-verify' ? 'Checking...' : 'Verify mobile'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="auth-password">Password</label>
            <input
              type="password"
              id="auth-password"
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleInputChange}
              className="input-field"
              required
            />
          </div>

          <button type="submit" className="btn btn-primary auth-submit-btn">
            {activeTab === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p className="auth-modal-footer-notice">
          {activeTab === 'login' ? (
            <>
              New to PallyWear?{' '}
              <span className="auth-footer-toggle" onClick={() => setActiveTab('register')}>
                Create an account
              </span>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <span className="auth-footer-toggle" onClick={() => setActiveTab('login')}>
                Sign in
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
